'use client';

import { createClientLogger, logClientWarning } from '@/client/logging/client-logger';
import { createJobsRepository } from '@/container/jobs-repository';
import { QUEUE_JOBS, QUEUE_NAMES } from '@/constants/queue';
import {
    EnqueueReviewAiAnswerJob,
    EnqueueReviewAiAnswerJobContext,
    useRunReviewAiAnswerJob,
} from '@/hooks/use-run-review-ai-answer-job';
import type { ReviewAiJobMessageLocale } from '@/shared/review-ai-job-messages';
import { toast } from '@/ui/toast';
import { createReviewAiAnswerToastId } from '@/ui/toast-ids';
import { useCallback, useEffect, useMemo, useRef } from 'react';

export type CancelableReviewAiAnswerJobName =
    | typeof QUEUE_JOBS.generateReviewAiAnswer
    | typeof QUEUE_JOBS.generateReviewAiAnswerManual;

export type ReviewAiAnswerJobCancellationReason =
    | 'dialog_closed'
    | 'component_unmounted'
    | 'request_superseded'
    | 'target_changed'
    | 'draft_changed'
    | 'template_applied'
    | 'answer_submitted';

export type CancelableReviewAiAnswerJobRunInput = {
    operationId: string;
    enqueue: EnqueueReviewAiAnswerJob;
    locale?: ReviewAiJobMessageLocale;
};

type ActiveReviewAiAnswerRun = {
    id: number;
    operationId: string;
    jobName: CancelableReviewAiAnswerJobName;
    abortController: AbortController;
    rejectCanceled: (error: ReviewAiAnswerJobCanceledError) => void;
    cancelRequested: boolean;
};

type UseCancelableReviewAiAnswerJobOptions = {
    accessToken: string;
    jobName: CancelableReviewAiAnswerJobName;
    locale?: ReviewAiJobMessageLocale;
};

const reviewAiAnswerCancelLogger = createClientLogger({
    scope: 'review-ai-answer-cancel',
});
const REVIEW_AI_ANSWER_CANCELED_MESSAGE = 'Генерация ИИ-ответа остановлена';

export class ReviewAiAnswerJobCanceledError extends Error {
    readonly operationId: string;
    readonly jobName: CancelableReviewAiAnswerJobName;
    readonly reason: ReviewAiAnswerJobCancellationReason;

    constructor({
        operationId,
        jobName,
        reason,
    }: {
        operationId: string;
        jobName: CancelableReviewAiAnswerJobName;
        reason: ReviewAiAnswerJobCancellationReason;
    }) {
        super('Review AI answer job was canceled.');
        this.name = 'ReviewAiAnswerJobCanceledError';
        this.operationId = operationId;
        this.jobName = jobName;
        this.reason = reason;
    }
}

export const isReviewAiAnswerJobCanceledError = (error: unknown): error is ReviewAiAnswerJobCanceledError =>
    error instanceof ReviewAiAnswerJobCanceledError ||
    (error instanceof Error && error.name === 'ReviewAiAnswerJobCanceledError');

export const useCancelableReviewAiAnswerJob = ({
    accessToken,
    jobName,
    locale = 'ru',
}: UseCancelableReviewAiAnswerJobOptions) => {
    const runReviewAiAnswerJob = useRunReviewAiAnswerJob();
    const jobsRepository = useMemo(() => createJobsRepository(), []);
    const activeRunRef = useRef<ActiveReviewAiAnswerRun | null>(null);
    const nextRunIdRef = useRef(0);

    const cancelRun = useCallback(
        async (activeRun: ActiveReviewAiAnswerRun, reason: ReviewAiAnswerJobCancellationReason): Promise<void> => {
            if (activeRun.cancelRequested) {
                return;
            }

            activeRun.cancelRequested = true;
            activeRun.rejectCanceled(
                new ReviewAiAnswerJobCanceledError({
                    operationId: activeRun.operationId,
                    jobName: activeRun.jobName,
                    reason,
                }),
            );
            activeRun.abortController.abort();

            try {
                await jobsRepository.cancel(
                    {
                        jobNames: [activeRun.jobName],
                        queueName: QUEUE_NAMES.aiAnalysis,
                        operationId: activeRun.operationId,
                    },
                    { authToken: accessToken },
                );
                toast.warning(REVIEW_AI_ANSWER_CANCELED_MESSAGE, {
                    autoClose: false,
                    toastId: createReviewAiAnswerToastId({
                        operationId: activeRun.operationId,
                        outcome: 'canceled',
                    }),
                });
            } catch (error) {
                logClientWarning({
                    logger: reviewAiAnswerCancelLogger,
                    message: '[client.review-ai-answer.cancel] Failed to cancel active review AI answer job.',
                    error,
                    context: {
                        operationId: activeRun.operationId,
                        jobName: activeRun.jobName,
                        queueName: QUEUE_NAMES.aiAnalysis,
                        reason,
                    },
                });
            } finally {
                if (activeRunRef.current?.id === activeRun.id) {
                    activeRunRef.current = null;
                }
            }
        },
        [accessToken, jobsRepository],
    );

    const cancelActive = useCallback(
        async (reason: ReviewAiAnswerJobCancellationReason): Promise<void> => {
            const activeRun = activeRunRef.current;
            if (!activeRun) {
                return;
            }

            await cancelRun(activeRun, reason);
        },
        [cancelRun],
    );

    const run = useCallback(
        async ({ operationId, enqueue, locale: runLocale }: CancelableReviewAiAnswerJobRunInput): Promise<string> => {
            const previousRun = activeRunRef.current;
            if (previousRun) {
                void cancelRun(previousRun, 'request_superseded');
            }

            const abortController = new AbortController();
            let rejectCanceled: ActiveReviewAiAnswerRun['rejectCanceled'] = () => undefined;
            const cancellationPromise = new Promise<never>((_resolve, reject) => {
                rejectCanceled = reject;
            });
            const activeRun: ActiveReviewAiAnswerRun = {
                id: nextRunIdRef.current + 1,
                operationId,
                jobName,
                abortController,
                rejectCanceled,
                cancelRequested: false,
            };
            nextRunIdRef.current = activeRun.id;
            activeRunRef.current = activeRun;

            const enqueueWithSignal = (resolvedOperationId: string, context: EnqueueReviewAiAnswerJobContext) =>
                enqueue(resolvedOperationId, { ...context, signal: abortController.signal });

            try {
                return await Promise.race([
                    runReviewAiAnswerJob({
                        operationId,
                        enqueue: enqueueWithSignal,
                        locale: runLocale ?? locale,
                        signal: abortController.signal,
                    }),
                    cancellationPromise,
                ]);
            } finally {
                if (activeRunRef.current?.id === activeRun.id) {
                    activeRunRef.current = null;
                }
            }
        },
        [cancelRun, jobName, locale, runReviewAiAnswerJob],
    );

    useEffect(() => {
        return () => {
            void cancelActive('component_unmounted');
        };
    }, [cancelActive]);

    return useMemo(
        () => ({
            run,
            cancelActive,
        }),
        [cancelActive, run],
    );
};
